import { create } from 'zustand'
import type {
  AvatarSessionState,
  AvatarInfo,
  AvatarProviderType,
  ExpressionType,
} from '@/connectors/avatar'

interface AvatarState {
  // Provider state
  isInitialized: boolean
  provider: AvatarProviderType | null
  avatars: AvatarInfo[]

  // Session state
  sessionState: AvatarSessionState
  isConnected: boolean
  isSpeaking: boolean
  currentExpression: ExpressionType

  // Video state
  videoElement: HTMLVideoElement | null
  isVideoReady: boolean

  // Transcription
  currentTranscript: string
  agentMessage: string

  // Settings
  videoQuality: '720p' | '1080p'
  enableEmotions: boolean

  // Actions
  setInitialized: (initialized: boolean, provider: AvatarProviderType) => void
  setAvatars: (avatars: AvatarInfo[]) => void
  setSessionState: (state: AvatarSessionState) => void
  setVideoElement: (element: HTMLVideoElement | null) => void
  setVideoReady: (ready: boolean) => void
  setCurrentTranscript: (transcript: string) => void
  setAgentMessage: (message: string) => void
  setExpression: (expression: ExpressionType) => void
  setVideoQuality: (quality: '720p' | '1080p') => void
  setEnableEmotions: (enabled: boolean) => void
  reset: () => void
}

const initialState = {
  isInitialized: false,
  provider: null as AvatarProviderType | null,
  avatars: [] as AvatarInfo[],
  sessionState: 'idle' as AvatarSessionState,
  isConnected: false,
  isSpeaking: false,
  currentExpression: 'neutral' as ExpressionType,
  videoElement: null as HTMLVideoElement | null,
  isVideoReady: false,
  currentTranscript: '',
  agentMessage: '',
  videoQuality: '1080p' as const,
  enableEmotions: true,
}

export const useAvatarStore = create<AvatarState>()((set) => ({
  ...initialState,

  setInitialized: (initialized, provider) =>
    set({ isInitialized: initialized, provider }),

  setAvatars: (avatars) => set({ avatars }),

  setSessionState: (state) =>
    set({
      sessionState: state,
      isConnected: state === 'connected' || state === 'ready' || state === 'speaking',
      isSpeaking: state === 'speaking',
    }),

  setVideoElement: (element) => set({ videoElement: element }),

  setVideoReady: (ready) => set({ isVideoReady: ready }),

  setCurrentTranscript: (transcript) => set({ currentTranscript: transcript }),

  setAgentMessage: (message) => set({ agentMessage: message }),

  setExpression: (expression) => set({ currentExpression: expression }),

  setVideoQuality: (quality) => set({ videoQuality: quality }),

  setEnableEmotions: (enabled) => set({ enableEmotions: enabled }),

  reset: () => set(initialState),
}))
